import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../hooks";
import { useTherapistIdentity } from "../../../../../pages/portal/Page_therapist/Page_Therapist_Salas/controller/useTherapistIdentity";

import { NewReviTherapyApi } from "../../../../../api/revi/NewReviTherapyApi";
import { PatientReviApi } from "../../../../../api/revi/patientApi";
import { ActiveSessionApi } from "../../../../../api/revi/activeSessionApi";

const therapyApi = new NewReviTherapyApi();
const patientApi = new PatientReviApi();
const sessionApi = new ActiveSessionApi();

/**
 * ✅ Custom hook (nombre correcto) para debounce
 */
function useDebouncedValue(value, ms = 350) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);

  return debounced;
}

function mapSexoFilterToApi(sexo) {
  // UI: ALL | M | F | O
  if (!sexo || sexo === "ALL") return undefined;
  return sexo; // "M" | "F" | "O"
}

async function fetchMyTherapies(token, therapistId, { page, limit, search }) {
  if (typeof therapyApi.getMyTherapies === "function") {
    return therapyApi.getMyTherapies(token, therapistId, {
      page,
      limit,
      search,
    });
  }
  if (typeof therapyApi.getTherapiesByTherapist === "function") {
    return therapyApi.getTherapiesByTherapist(
      token,
      therapistId,
      page,
      limit,
      search,
    );
  }
  // Diagnostico de desarrollo, NO interfaz: significa que falta un metodo en
  // el cliente de la API. Antes acababa en un message.error en castellano
  // delante del terapeuta; ahora se marca como tal para que quien lo lea sepa
  // que es un fallo de programacion, no un problema suyo.
  throw new Error("[revi] therapyApi: falta el metodo de listar terapias");
}

async function fetchTherapyById(token, therapyId) {
  if (typeof therapyApi.getTherapyById === "function") {
    return therapyApi.getTherapyById(token, therapyId);
  }
  if (typeof therapyApi.getTherapy === "function") {
    return therapyApi.getTherapy(token, therapyId);
  }
  throw new Error(
    "[revi] therapyApi: falta el metodo de obtener terapia por id",
  );
}

export function useImpartirTerapiaWizardController({ open, onCreated }) {
  // Los avisos que salen de aqui se leen en pantalla: son interfaz.
  const { t } = useTranslation("therapy");

  // ✅ Asegúrate de obtener también user para therapistId
  const { accessToken, user } = useAuth();
  const { therapistId } = useTherapistIdentity(user);

  const [step, setStep] = useState(0);
  const [loadingPrimary, setLoadingPrimary] = useState(false);
  // Guarda de en-vuelo: sin ella, dos toques seguidos crean DOS sesiones y DOS
  // salas VR. En tactil el doble toque es el gesto normal, pero tambien pasa
  // con raton si el servidor tarda.
  const [creating, setCreating] = useState(false);

  // Step 1
  const [therapySearch, setTherapySearch] = useState("");
  const debouncedTherapySearch = useDebouncedValue(therapySearch, 350);
  const [therapies, setTherapies] = useState([]);
  const [selectedTherapyId, setSelectedTherapyId] = useState(null);

  // Step 2
  const [patientSearch, setPatientSearch] = useState("");
  const debouncedPatientSearch = useDebouncedValue(patientSearch, 350);
  const [sexo, setSexo] = useState("ALL");
  const [patients, setPatients] = useState([]);
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);

  // Step 3
  const [therapyDetail, setTherapyDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const reset = useCallback(() => {
    setStep(0);

    setTherapySearch("");
    setTherapies([]);
    setSelectedTherapyId(null);

    setPatientSearch("");
    setSexo("ALL");
    setPatients([]);
    setSelectedPatientIds([]);

    setTherapyDetail(null);
    setLoadingPrimary(false);
    setLoadingDetail(false);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  // Fetch Step 1 therapies
  useEffect(() => {
    if (!open || step !== 0) return;
    if (!accessToken || !therapistId) return;

    let alive = true;

    (async () => {
      try {
        setLoadingPrimary(true);

        const res = await fetchMyTherapies(accessToken, therapistId, {
          page: 1,
          limit: 30,
          search: debouncedTherapySearch,
        });

        if (!alive) return;
        setTherapies(res?.therapies || res?.data?.therapies || []);
      } catch (e) {
        message.error(e?.message || t("wizard.avisos.errorTerapias"));
      } finally {
        if (alive) setLoadingPrimary(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, step, accessToken, therapistId, debouncedTherapySearch, t]);

  // Fetch Step 2 patients
  useEffect(() => {
    if (!open || step !== 1) return;
    if (!accessToken) return;

    let alive = true;

    (async () => {
      try {
        setLoadingPrimary(true);

        const res = await patientApi.getMyForTherapist(accessToken, {
          page: 1,
          limit: 60,
          search: debouncedPatientSearch,
          sexo: mapSexoFilterToApi(sexo),
        });

        if (!alive) return;
        setPatients(res?.patients || []);
      } catch (e) {
        message.error(e?.message || t("wizard.avisos.errorPacientes"));
      } finally {
        if (alive) setLoadingPrimary(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, step, accessToken, debouncedPatientSearch, sexo, t]);

  // Fetch Step 3 detail therapy
  useEffect(() => {
    if (!open || step !== 2) return;
    if (!accessToken || !selectedTherapyId) return;

    let alive = true;

    (async () => {
      try {
        setLoadingDetail(true);

        const res = await fetchTherapyById(accessToken, selectedTherapyId);
        if (!alive) return;

        setTherapyDetail(res?.therapy || res?.data?.therapy || res);
      } catch (e) {
        message.error(e?.message || t("wizard.avisos.errorDetalle"));
        setTherapyDetail(null);
      } finally {
        if (alive) setLoadingDetail(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, step, accessToken, selectedTherapyId, t]);

  const togglePatient = useCallback((patientId) => {
    setSelectedPatientIds((prev) =>
      prev.includes(patientId)
        ? prev.filter((id) => id !== patientId)
        : [...prev, patientId],
    );
  }, []);

  const toggleManyPatients = useCallback((patientIds, checked) => {
    setSelectedPatientIds((prev) => {
      const set = new Set(prev);
      if (checked) patientIds.forEach((id) => set.add(id));
      else patientIds.forEach((id) => set.delete(id));
      return Array.from(set);
    });
  }, []);

  const canNext = useMemo(() => {
    if (step === 0) return !!selectedTherapyId;
    if (step === 1) return selectedPatientIds.length > 0;
    return true;
  }, [step, selectedTherapyId, selectedPatientIds]);

  const confirmCreateSession = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      // Estos Error se acaban leyendo en el message.error de abajo, asi que su
      // texto tambien es interfaz.
      if (!accessToken) throw new Error(t("wizard.avisos.noAutenticado"));
      if (!selectedTherapyId)
        throw new Error(t("wizard.avisos.seleccionaTerapia"));
      if (!selectedPatientIds.length)
        throw new Error(t("wizard.avisos.seleccionaPaciente"));

      const res = await sessionApi.initSession(accessToken, {
        therapyId: selectedTherapyId,
        patients: selectedPatientIds,
      });

      const sessionId = res?.session?._id || res?.data?.session?._id;
      if (!sessionId) throw new Error(t("wizard.avisos.sinSessionId"));

      message.success(t("wizard.avisos.creada"));
      onCreated?.(sessionId);
    } catch (e) {
      message.error(e?.message || t("wizard.avisos.errorIniciar"));
    } finally {
      setCreating(false);
    }
  }, [
    accessToken,
    selectedTherapyId,
    selectedPatientIds,
    onCreated,
    creating,
    t,
  ]);

  return {
    step,
    setStep,
    canNext,
    loadingPrimary,
    creating,
    reset,
    confirmCreateSession,

    therapies,
    therapySearch,
    setTherapySearch,
    selectedTherapyId,
    setSelectedTherapyId,

    patients,
    patientSearch,
    setPatientSearch,
    sexo,
    setSexo,
    selectedPatientIds,
    togglePatient,
    toggleManyPatients,

    therapyDetail,
    loadingDetail,
  };
}
